import { useMemo } from "react";
import type { SetURLSearchParams } from "react-router";
import { type DocumentSummary } from "~/api/types";
import { useTags } from "~/api/swr/useTags";
import { useTiers } from "~/api/swr/useTiers";
import {
  ArchiveState,
  buildFilterParamKeysForField,
  buildSearchParamValuesFromFilters,
  dateFilterKeys,
  nullableDateFilterKeys,
  FilterOperatorEnum,
  type FilterCondition,
  type DateFilterKey,
  type FilterField,
  type FilterOperator,
  type NullableDateFilterKey,
  isFilterFieldValueValid,
  isNullableDateFilterKey,
  type MultiSelectFilterKey,
  parseFilters,
  type SelectFieldData,
} from "../helpers/documentFilters";
import { printFullName } from "../helpers/user";
import { getNoTierLabel, NO_TIER_VALUE } from "~/helpers/tier";
import { useTranslation } from "react-i18next";

interface UseDocumentFilterParams {
  documents: DocumentSummary[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  multiSelectFields?: readonly MultiSelectFilterKey[];
  dateFields?: readonly DateFilterKey[];
}

const libraryMultiSelectFilterKeys = [
  "archiveState",
  "tier",
  "category",
  "tag",
  "owner",
  "publisher",
] as const satisfies readonly MultiSelectFilterKey[];

export interface DocumentFiltersModel {
  dateFilterKeys: readonly DateFilterKey[];
  multiSelectFilterKeys: readonly MultiSelectFilterKey[];
  nullableDateFilterKeys: typeof nullableDateFilterKeys;
  multiSelectFieldData: Record<MultiSelectFilterKey, SelectFieldData>;
  activeFilters: FilterCondition[];
  getFilterValue: (
    field: FilterField,
    operator: FilterOperator,
  ) => string | undefined;
  setFilterValue: (
    field: FilterField,
    operator: FilterOperator,
    value?: string,
  ) => void;
  getInValues: (field: MultiSelectFilterKey) => string[];
  setInValues: (field: MultiSelectFilterKey, values: string[]) => void;
  isNullFilterActive: (field: NullableDateFilterKey) => boolean;
  setNullFilter: (field: NullableDateFilterKey, active: boolean) => void;
  isDateRangeInvalid: (filterKey: DateFilterKey) => boolean;
  removeFilter: (field: FilterField) => void;
}

type ActiveFilterValuesByField = Partial<
  Record<FilterField, Partial<Record<FilterOperator, string>>>
>;

const buildSelectFieldData = (
  entries: Array<[string, string]>,
): SelectFieldData => {
  const labelsByValue = entries.reduce<Record<string, string>>(
    (acc, [value, label]) => {
      acc[value] = label;
      return acc;
    },
    {},
  );

  return {
    labelsByValue,
    values: Object.keys(labelsByValue).sort((left, right) =>
      labelsByValue[left]!.localeCompare(labelsByValue[right]!),
    ),
  };
};

export const useDocumentFilters = ({
  documents,
  searchParams,
  setSearchParams,
  multiSelectFields = libraryMultiSelectFilterKeys,
  dateFields = dateFilterKeys,
}: UseDocumentFilterParams): DocumentFiltersModel => {
  const { data: tags } = useTags();
  const { data: tiers } = useTiers();
  const { t } = useTranslation();

  const activeFilters = useMemo(
    () =>
      parseFilters(searchParams, {
        multiSelectFields,
        dateFields,
      }),
    [searchParams, multiSelectFields, dateFields],
  );
  const activeFilterValuesByField = useMemo(
    () =>
      activeFilters.reduce<ActiveFilterValuesByField>((acc, filter) => {
        if (typeof filter.value !== "string") {
          return acc;
        }

        acc[filter.field] = {
          ...(acc[filter.field] ?? {}),
          [filter.operator]: filter.value,
        };
        return acc;
      }, {}),
    [activeFilters],
  );

  const multiSelectFieldData = useMemo<
    Record<MultiSelectFilterKey, SelectFieldData>
  >(
    () => ({
      archiveState: {
        values: [ArchiveState.active, ArchiveState.archived],
        labelsByValue: {
          [ArchiveState.active]: t(($) => $.commons.documentStatus.active),
          [ArchiveState.archived]: t(($) => $.commons.documentStatus.archived),
        },
      },
      tier: [
        ...(tiers ?? []).map((tier) => ({
          value: tier.id.toString(),
          label: tier.title,
        })),
        { value: NO_TIER_VALUE, label: getNoTierLabel(t) },
      ].reduce<SelectFieldData>(
        (acc, option) => {
          acc.values.push(option.value);
          acc.labelsByValue[option.value] = option.label;
          return acc;
        },
        { values: [], labelsByValue: {} },
      ),
      category: buildSelectFieldData(
        documents.map((doc) => [
          doc.category.id.toString(),
          doc.category.title,
        ]),
      ),
      tag: buildSelectFieldData(
        (tags ?? []).map((tag) => [tag.id.toString(), tag.title]),
      ),
      owner: buildSelectFieldData(
        documents.map((doc) => [
          doc.owner.userId.toString(),
          printFullName(doc.owner),
        ]),
      ),
      publisher: buildSelectFieldData(
        documents.flatMap((doc) => {
          const publisher = doc.publisher;

          if (!publisher) {
            return [];
          }

          return [[publisher.userId.toString(), printFullName(publisher)]];
        }),
      ),
    }),
    [documents, tags, tiers, t],
  );

  const setSearchParamsValues = (
    values: Record<string, string | string[] | undefined>,
  ) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(values).forEach(([key, value]) => {
      next.delete(key);
      if (value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v.length > 0) next.append(key, v);
        });
      } else if (value.length > 0) {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const getFilterValue = (field: FilterField, operator: FilterOperator) =>
    activeFilterValuesByField[field]?.[operator];

  const setFilterValue = (
    field: FilterField,
    operator: FilterOperator,
    value?: string,
  ) => {
    let nextFilters = activeFilters.filter(
      (filter) => !(filter.field === field && filter.operator === operator),
    );

    if (
      isNullableDateFilterKey(field) &&
      operator !== FilterOperatorEnum.isNull
    ) {
      nextFilters = nextFilters.filter(
        (filter) =>
          !(
            filter.field === field &&
            filter.operator === FilterOperatorEnum.isNull
          ),
      );
    }

    if (value && value.length > 0) {
      if (!isFilterFieldValueValid(field, value)) {
        return;
      }

      nextFilters.push({
        field,
        operator,
        value,
      });
    }

    setSearchParamsValues(buildSearchParamValuesFromFilters(nextFilters));
  };

  const getInValues = (field: MultiSelectFilterKey) => {
    const eqFilter = activeFilters.find(
      (filter) =>
        filter.field === field && filter.operator === FilterOperatorEnum.eq,
    );
    if (eqFilter && typeof eqFilter.value === "string") {
      return [eqFilter.value];
    }

    const inFilter = activeFilters.find(
      (filter) =>
        filter.field === field && filter.operator === FilterOperatorEnum.in,
    );
    if (inFilter && Array.isArray(inFilter.value)) {
      return inFilter.value;
    }

    return [];
  };

  const setInValues = (field: MultiSelectFilterKey, values: string[]) => {
    const nextFilters = activeFilters.filter(
      (filter) =>
        !(
          filter.field === field &&
          (filter.operator === FilterOperatorEnum.eq ||
            filter.operator === FilterOperatorEnum.in)
        ),
    );

    if (values.length === 1) {
      const [singleValue] = values;
      if (!singleValue) {
        throw new Error("Missing value for eq operator");
      }

      nextFilters.push({
        field,
        operator: FilterOperatorEnum.eq,
        value: singleValue,
      });
    } else if (values.length > 1) {
      nextFilters.push({
        field,
        operator: FilterOperatorEnum.in,
        value: values,
      });
    }

    setSearchParamsValues(buildSearchParamValuesFromFilters(nextFilters));
  };

  const isNullFilterActive = (field: NullableDateFilterKey) =>
    activeFilters.some(
      (filter) =>
        filter.field === field && filter.operator === FilterOperatorEnum.isNull,
    );

  const setNullFilter = (field: NullableDateFilterKey, active: boolean) => {
    const nextFilters = activeFilters.filter(
      (filter) =>
        !(
          filter.field === field &&
          (active || filter.operator === FilterOperatorEnum.isNull)
        ),
    );
    if (active) {
      nextFilters.push({
        field,
        operator: FilterOperatorEnum.isNull,
        value: "null",
      });
    }
    setSearchParamsValues(buildSearchParamValuesFromFilters(nextFilters));
  };

  const isDateRangeInvalid = (filterKey: DateFilterKey) => {
    const gte = getFilterValue(filterKey, FilterOperatorEnum.gte);
    const lte = getFilterValue(filterKey, FilterOperatorEnum.lte);
    if (!gte || !lte) {
      return false;
    }

    if (
      !isFilterFieldValueValid(filterKey, gte) ||
      !isFilterFieldValueValid(filterKey, lte)
    ) {
      return false;
    }

    return new Date(gte) > new Date(lte);
  };

  const removeFilter = (field: FilterField) => {
    setSearchParamsValues(
      buildFilterParamKeysForField(field).reduce<
        Record<string, string | undefined>
      >((acc, paramKey) => {
        acc[paramKey] = undefined;
        return acc;
      }, {}),
    );
  };

  return {
    dateFilterKeys: dateFields,
    multiSelectFilterKeys: multiSelectFields,
    nullableDateFilterKeys,
    multiSelectFieldData,
    activeFilters,
    getFilterValue,
    setFilterValue,
    getInValues,
    setInValues,
    isNullFilterActive,
    setNullFilter,
    isDateRangeInvalid,
    removeFilter,
  };
};
